import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as cornerstone from "@cornerstonejs/core";
import { CrosshairsTool } from "@cornerstonejs/tools";
import ViewportContext from "../../../../context/studyviewer/ViewportContext";
import ThumbnailContext from "../../../../context/studyviewer/ThumbnailContext";
import toastContext from "../../../../context/toast/ToastContext";
import themeContext from "../../../../context/theme/ThemeContext";
import loaderContext from "../../../../context/loader/LoaderContext";
import { MPR_TOOL_OBJECT, TOAST_TYPE } from "../../../../constants/Constants";
import {
  enableVOISync,
  loadMPR,
  resizeViewport,
  exitMPRViewports,
  resizeViewportsAfterLayout,
} from "../../helpers/CornerstoneViewportService";
import {
  canAttemptMPR,
  isReconstructable,
  startLoaderIfSlow,
  waitForNextPaint,
} from "../../helpers/MPRService";
import MPR from "../../../../components/SVGComponents/Tools/MPR";
import DropDown from "../../../../components/SVGComponents/Tools/DropDown";
import { toolStyle } from "../../styles/styles";

// Mobile MPR entry: a toolbar button that opens an anchored dropdown to pick a
// single plane (Axial / Coronal / Sagittal) or Close MPR. Reuses the same
// toggleMPR / plane-switch / exit logic as the desktop MPRToolComponent & MPRTool.
// OLD: const MobileMprMenu = () => {
// NEW: accept an optional onAction() callback so a parent menu (e.g. the More
//      grid) can close itself after a plane is selected or MPR is closed.
const MobileMprMenu = ({ onAction }) => {
  const { t } = useTranslation();
  const { theme } = useContext(themeContext);
  const { showToastv2 } = useContext(toastContext);
  const { ref } = useContext(loaderContext);
  const { setActiveMPRThumbnailIndex, activeThumbnailIndex, activeModality } =
    useContext(ThumbnailContext);
  const {
    isMPRActive,
    setIsMPRActive,
    setActiveMPRTool,
    isAxialActive,
    setIsAxialActive,
    isSagittalActive,
    setIsSagittalActive,
    isCoronalActive,
    setIsCoronalActive,
    activeViewportIndex,
    viewportSeriesData,
    setMprLoading,
    setMprViewportElements,
    setIsMPRViewportActivated,
    setMPRViewportLoaderRunning,
    setVolumeId,
    isMPREnabled,
  } = useContext(ViewportContext);

  const [showMenu, setShowMenu] = useState(false);
  // Toolbar is at the BOTTOM on mobile, so the dropdown opens UPWARD: we anchor
  // its bottom edge just above the button (using `bottom`, not `top`).
  const [menuPos, setMenuPos] = useState({ bottom: 0, left: 0 });
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  // Open/close and, on open, anchor the dropdown directly below the MPR button
  // using its live position (fixed positioning avoids the toolbar's horizontal
  // scroll clipping the menu).
  const toggleMenu = () => {
    setShowMenu((prev) => {
      const next = !prev;
      if (next && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setMenuPos({
          bottom: window.innerHeight - rect.top + 6,
          left: rect.left,
        });
      }
      return next;
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, []);

  // Same as toggleMPR() in MPRToolComponent: no-op when MPR already active,
  // otherwise run the reconstructable check and load the MPR volume.
  const toggleMPR = async (tool) => {
    if (isMPRActive) {
      return;
    }

    // Two-phase entry, deliberately:
    //
    // Phase 1 — a cheap first-slice-only gate (canAttemptMPR) so a series that
    // can never be reconstructed at all (wrong modality, single slice, one
    // slice already too big for the volume budget) is rejected without the
    // layout ever flipping into MPR and straight back out.
    //
    // Phase 2 — flip into MPR and let React paint the 3-pane layout WITH its
    // spinner before the expensive whole-series geometry check and volume load
    // run. Doing that heavy work ahead of the flip (as this used to) blocked
    // the main thread while nothing on screen had changed yet: the click looked
    // like it had done nothing at all, and then the viewer jumped straight to
    // fully rendered MPR images without the loader ever being visible.
    const stopGateLoader = startLoaderIfSlow(ref);
    let canAttempt;
    try {
      canAttempt = await canAttemptMPR(activeViewportIndex, "viewport");
    } catch (error) {
      console.error("[MPR] reconstructability pre-check failed:", error);
      canAttempt = { value: false };
    } finally {
      stopGateLoader();
    }

    if (!canAttempt.value) {
      showToastv2(TOAST_TYPE.ERROR, t("mpr.not-reconstructable"));
      return;
    }

    setMPRViewportLoaderRunning(true);
    setActiveMPRTool(CrosshairsTool.toolName);
    setActiveMPRThumbnailIndex(activeThumbnailIndex);
    setIsMPRActive(true);
    // Hand the frame back so the MPR panes are on screen (spinner running)
    // before anything expensive starts.
    await waitForNextPaint();

    const isSeriesReconstructable = await isReconstructable(
      activeViewportIndex,
      "viewport"
    );

    if (!isSeriesReconstructable.value) {
      // Only the full geometry check can reject on spacing/orientation, so a
      // series that cleared phase 1 and fails here has to be backed out of
      // MPR mode again.
      setIsMPRActive(false);
      setMPRViewportLoaderRunning(false);
      showToastv2(TOAST_TYPE.ERROR, t("mpr.not-reconstructable"));
      return;
    }
    if (isSeriesReconstructable.hasDuplicateSlice) {
      showToastv2(TOAST_TYPE.WARNING, t("mpr.duplicate-slice-warning"));
    }
    if (isSeriesReconstructable.excludedImageIds?.length) {
      showToastv2(TOAST_TYPE.WARNING, t("mpr.excluded-instances-warning"));
    }

    (async () => {
      try {
        await loadMPR(
          activeViewportIndex,
          "viewport",
          viewportSeriesData[activeViewportIndex].series,
          setMprLoading,
          setMprViewportElements,
          setIsMPRViewportActivated,
          setVolumeId,
          isSeriesReconstructable.excludedImageIds
        );
      } catch (error) {
        console.error("[MPR] failed to load volume:", error);
        setIsMPRActive(false);
        setMPRViewportLoaderRunning(false);
        showToastv2(TOAST_TYPE.ERROR, t("mpr.load-failed"));
      }
    })();
    enableVOISync();
  };

  // Enter MPR (if needed) and show a single plane; when already active this just
  // switches the visible orientation instantly (no reload).
  const selectPlane = (plane) => {
    // MPR uses VolumeViewports, which require GPU/WebGL rendering. The mobile
    // viewer may be running in CPU-fallback mode where volumes cannot render.
    let hasWebGL2 = false;
    try {
      hasWebGL2 = !!(
        window.WebGL2RenderingContext &&
        document.createElement("canvas").getContext("webgl2")
      );
    } catch (e) {
      hasWebGL2 = false;
    }
    console.warn("::MPR-RENDER:: [selectPlane] tapped", {
      plane,
      isMPRActive,
      activeModality,
      isMPREnabled,
      getShouldUseCPURendering: cornerstone.getShouldUseCPURendering?.(),
      hasWebGL2,
    });
    if (!isMPRActive && cornerstone.getShouldUseCPURendering?.()) {
      console.warn(
        "::MPR-RENDER:: [selectPlane] BLOCKED by CPU-mode guard -> showing not-supported toast"
      );
      showToastv2(TOAST_TYPE.ERROR, t("mpr.not-supported-on-device"));
      setShowMenu(false);
      onAction?.(); // NEW: also close the parent More menu
      return;
    }
    toggleMPR();
    setIsAxialActive(plane === "axial");
    setIsSagittalActive(plane === "sagittal");
    setIsCoronalActive(plane === "coronal");
    resizeViewportsAfterLayout();
    setShowMenu(false);
    onAction?.(); // NEW: close the parent More menu after entering/switching plane
  };

  const closeMPR = async () => {
    // Must release the MPR viewports/volume, not just flip the flag —
    // otherwise the previous volume's GPU texture stays resident and the
    // next MPR activation has to coexist with it (see exitMPRViewports).
    // Mobile never activates the 3D pane, so no is3DActive/setIs3DActive here.
    await exitMPRViewports(false, undefined, setIsMPRActive);
    setShowMenu(false);
    onAction?.(); // NEW: close the parent More menu after closing MPR
  };

  if (MPR_TOOL_OBJECT.NotFor.includes(activeModality)) {
    return null;
  }

  const disabled = !isMPREnabled;
  const activeColor = theme.palette.activeTool;
  const inactiveColor = theme.palette.inactiveTool;

  const PLANES = [
    { key: "axial", label: t("mpr.axial"), active: isAxialActive },
    { key: "coronal", label: t("mpr.coronal"), active: isCoronalActive },
    { key: "sagittal", label: t("mpr.sagittal"), active: isSagittalActive },
  ];

  return (
    <div ref={menuRef} style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div
        ref={buttonRef}
        onClick={disabled ? undefined : toggleMenu}
        style={{ ...toolStyle, opacity: disabled ? 0.4 : 1, pointerEvents: disabled ? "none" : "auto" }}
      >
        <div
          style={{
            height: "65%",
            width: "60px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "4px",
          }}
        >
          <div className="highlight-tool-mobile">
            {/* OLD: <MPR color={...} /> rendered at its native 40x32 (with its
                 own border box), noticeably larger than the other ~24px tiles.
                NEW: pass iconStyle to scale it down to ~30x24 so it matches. */}
            <MPR
              color={isMPRActive || showMenu ? activeColor : inactiveColor}
              iconStyle={{ width: "30px", height: "24px" }}
            />
          </div>
          <DropDown up color={isMPRActive || showMenu ? activeColor : inactiveColor} />
        </div>
        <span
          style={{
            fontSize: "9px",
            lineHeight: "100%",
            fontWeight: "400",
            color: "var(--mobile-color-txt)",
            display: "block",
            width: "60px",
            textAlign: "center",
            // match the other tool tiles' icon↔label gap (labelStyle uses 4px).
            marginTop: "4px",
          }}
        >
          {t("viewer.tools.mpr")}
        </span>
      </div>

      {showMenu && (
        <div
          className="viewer-tool-more-dropdown viewer-tool-more-dropdown-height"
          style={{
            position: "fixed",
            bottom: menuPos.bottom,
            left: menuPos.left,
            zIndex: 9999,
            minWidth: "150px",
            display: "flex",
            flexDirection: "column",
            borderRadius: "5px",
            paddingTop: "6px",
            paddingBottom: "6px",
            boxShadow:
              "0px 0.6px 1.8px rgba(0, 0, 0, 0.1), 0px 3.2px 7.2px rgba(0, 0, 0, 0.13)",
          }}
        >
          {PLANES.map((plane) => (
            <div
              key={plane.key}
              className="stack-scroll-dropdown-elements"
              style={{
                padding: "8px 14px",
                cursor: "pointer",
                background:
                  isMPRActive && plane.active
                    ? "rgba(255, 184, 1, 0.12)"
                    : "transparent",
              }}
              onClick={() => selectPlane(plane.key)}
            >
              {plane.label}
            </div>
          ))}
          {isMPRActive && (
            <div
              className="stack-scroll-dropdown-elements"
              style={{ padding: "8px 14px", cursor: "pointer" }}
              onClick={closeMPR}
            >
              {t("mpr.close-mpr")}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MobileMprMenu;
